import {delay} from 'utils/delay'
import {loadRecaptchaEnterprise} from './loadRecaptchaEnterprise'
import {reportRecaptchaFailure} from './reportRecaptchaFailure'
import {RecaptchaAction} from './types'

export const RECAPTCHA_EXECUTE_ATTEMPTS = 3
export const RECAPTCHA_EXECUTE_RETRY_DELAY_MS = 300

/**
 * Generates a token immediately before the interaction it protects.
 *
 * Google tokens expire after two minutes and can normally be assessed only once, so
 * tokens are never generated in the background or reused across requests.
 *
 * These attempts happen before the Checkout API request and are separate from the
 * three-total-request limit in `withRecaptchaRetry`.
 */
export const createRecaptchaToken = async (siteKey: string, action: RecaptchaAction): Promise<string> => {
  await loadRecaptchaEnterprise(siteKey)

  let lastError: unknown

  for (let attempt = 1; attempt <= RECAPTCHA_EXECUTE_ATTEMPTS; attempt++) {
    try {
      const token = await window.grecaptcha!.enterprise!.execute!(siteKey, {action})

      if (typeof token !== 'string' || token.length === 0) {
        throw new Error('reCAPTCHA Enterprise returned an empty token')
      }

      return token
    } catch (error) {
      lastError = error

      if (attempt < RECAPTCHA_EXECUTE_ATTEMPTS) {
        await delay(RECAPTCHA_EXECUTE_RETRY_DELAY_MS)
      }
    }
  }

  reportRecaptchaFailure('recaptcha.token_failed', lastError, {action})
  throw lastError
}
